import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Plus, Filter, BarChart, Download } from 'react-feather';
import { Button, Card, Badge, Modal } from 'react-bootstrap';
import FilterWithTrashed from "../../filters/FilterWithTrashed";
import TableSpinner from '../../layout/TableSpinner';
import CustomPaginationWithManyPages from '../../layout/CustomPaginationWithManyPages';
import SearchForm from '../../layout/SearchForm';
import { usePermission } from '../../../hooks/usePermission';
import { getFirmOptionsForManagers } from '../../../actions/user';

// Actions
import { 
    getSparepartExpenses, 
    getExpenseStats,
    getExpenseData,
    exportSparepartExpenses
} from '../../../actions/sparepart_expense';

// Components (we'll create these)
import SparepartExpenseItem from './SparepartExpenseItem';
import SparepartExpenseCreateForm from './SparepartExpenseCreateForm';
import SparepartExpenseUpdateForm from './SparepartExpenseUpdateForm';
import SparepartExpenseDeleteForm from './SparepartExpenseDeleteForm';
import ExpenseFilters from './ExpenseFilters';
import ExpenseStatistics from './ExpenseStatistics';

// Create a DeletedExpensesFilterModal component
const DeletedExpensesFilterModal = ({ show, handleClose, getPageItems, dispatch, onlyTrashed }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan sarflar filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan sarflar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha sarflarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input 
                                className="form-check-input"
                                type="checkbox"
                                id="filterOnlyTrashedExpenses"
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    
                                    const url = new URL(window.location);
                                    url.searchParams.set('page', 1);
                                    
                                    if (checked) {
                                        url.searchParams.set('only_trashed', 'true');
                                        url.searchParams.delete('with_trashed');
                                    } else {
                                        url.searchParams.delete('only_trashed');
                                    }
                                    
                                    window.history.pushState({}, '', url);
                                    
                                    // Get updated params for API call
                                    const newParams = new URLSearchParams(window.location.search);
                                    const apiParams = {};
                                    for (const [key, value] of newParams.entries()) {
                                        apiParams[key] = value;
                                    }
                                    
                                    dispatch(getPageItems(1, apiParams));
                                }}
                                checked={onlyTrashed}
                            />
                            <label className="form-check-label" htmlFor="filterOnlyTrashedExpenses">
                                Faqat o'chirilgan sarflar
                            </label>
                        </div>
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan sarflarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const SparepartExpenses = () => {
    const { userCan } = usePermission();
    const dispatch = useDispatch();
    
    const {
        expenses,
        loading,
        numberOfPages,
        currentPage,
        total_expenses,
        expense_stats: statistics,
        error
    } = useSelector(state => state.sparepart_expense);

    const auth = useSelector(state => state.auth);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showStatistics, setShowStatistics] = useState(false);
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const [filters, setFilters] = useState({});
    const [hasInitialLoad, setHasInitialLoad] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    // Load options data for permission checks and filters
    useEffect(() => {
        if (auth.user?.role_name === 'manager' || auth.user?.role_name === 'accountant') {
            dispatch(getFirmOptionsForManagers());
        }
    }, [dispatch, auth.user?.role_name]);

    // Initial load effect - runs only once
    useEffect(() => {
        if (!hasInitialLoad && userCan('view sparepart expenses')) {
            dispatch(getSparepartExpenses(1, {}));
            dispatch(getExpenseStats());
            dispatch(getExpenseData());
            setHasInitialLoad(true);
        }
    }, [dispatch, userCan, hasInitialLoad]);

    // Filter changes effect - only runs after initial load and when filters actually change
    useEffect(() => {
        if (hasInitialLoad && Object.keys(filters).length > 0) {
            dispatch(getSparepartExpenses(1, filters));
        }
    }, [dispatch, hasInitialLoad, JSON.stringify(filters)]);

    const handleSearch = useCallback((page, params) => {
        // This function is called by SearchForm and expects to be dispatched
        // We need to return an action that can be dispatched
        return getSparepartExpenses(page, params);
    }, []);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);

    // Early return if no permission - placed after all hooks to comply with React Rules of Hooks
    if (!userCan('view sparepart expenses')) {
        return <div className="alert alert-warning">Sizda sarflarni ko'rish huquqi yo'q.</div>;
    }

    return (
        <Fragment>
            <div className="container-fluid p-0">
                {/* Header and Actions */}
                <div className="row mb-3 mt-3">
                    <div className="col-md-6">
                        <h4 className="mb-0">
                            Sarflar <Badge bg="secondary">({total_expenses})</Badge>
                        </h4>
                    </div>
                    <div className="col-md-6 text-end">
                        <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => setShowStatistics(!showStatistics)}
                        >
                            <BarChart size={16} className="me-1" />
                            Statistika
                        </Button>
                        
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="me-2"
                            onClick={() => dispatch(exportSparepartExpenses(filters))}
                            disabled={!filters.transport_id && !filters.machine_id}
                            title={!filters.transport_id && !filters.machine_id ? "Transport yoki uskunani tanlang" : "XLSX yuklab olish"}
                        >
                            <Download size={16} className="me-1" />
                            XLSX yuklab olish
                        </Button>

                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2"
                            onClick={handleDeletedFiltersShow}
                        >
                            <Filter size={16} className="me-1" />
                            O'chirilganlar
                            {hasDeletedFilters && <span className="badge bg-warning ms-1">1</span>}
                        </Button>
                        
                        {userCan('create sparepart expenses') && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowCreateForm(true)}
                            >
                                <Plus size={16} className="me-1" />
                                Sarfni ro'yxatga olish
                            </Button>
                        )}
                    </div>
                </div>

                {/* Statistics */}
                {showStatistics && (
                    <Card className="mb-3">
                        <Card.Body>
                            <ExpenseStatistics statistics={statistics} />
                        </Card.Body>
                    </Card>
                )}

                {/* Filters and Search */}
                <div className="row mb-3">
                    <div className="col-md-9">
                        <ExpenseFilters
                            filters={filters}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                    <div className="col-md-3">
                        <SearchForm 
                            getPageItems={handleSearch}
                            placeholder="Ehtiyot qism nomi bo'yicha qidirish..."
                        />
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="alert alert-danger">
                        {error.message || 'Xatolik yuz berdi'}
                    </div>
                )}

                {/* Expenses Table */}
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body p-0">
                                {(loading || isLoading) && <TableSpinner />}
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">#</th>
                                                <th scope="col">Ehtiyot qism</th>
                                                <th scope="col">Miqdor</th>
                                                <th scope="col">Narx</th>
                                                <th scope="col">Jami summa</th>
                                                <th scope="col">Ombor</th>
                                                <th scope="col">Sarflagan shaxs</th>
                                                <th scope="col">Transport/Uskuna</th>
                                                <th scope="col">Sana</th>
                                                <th scope="col">Izohlar</th>
                                                <th scope="col">Amallar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {expenses && expenses.length > 0 ? (
                                                expenses.map((expense, index) => (
                                                    <SparepartExpenseItem
                                                        key={expense.id}
                                                        expense={expense}
                                                        number={(currentPage - 1) * 15 + index + 1}
                                                    />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {loading || isLoading ? 'Ma\'lumotlar yuklanmoqda...' : 'Sarflar topilmadi'}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                {numberOfPages > 1 && (
                    <div className="row mt-3">
                        <div className="col-12">
                            <CustomPaginationWithManyPages
                                numberOfPages={numberOfPages}
                                getPageItems={getSparepartExpenses}
                                current_page={currentPage}
                                custom_params={filters}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {userCan('create sparepart expenses') && (
                <SparepartExpenseCreateForm
                    show={showCreateForm}
                    handleClose={() => setShowCreateForm(false)}
                />
            )}

            {userCan('edit sparepart expenses') && (
                <SparepartExpenseUpdateForm />
            )}

            {userCan('delete sparepart expenses') && (
                <SparepartExpenseDeleteForm />
            )}

            <DeletedExpensesFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getSparepartExpenses}
                dispatch={dispatch}
                onlyTrashed={onlyTrashed}
            />
        </Fragment>
    );
};

export default SparepartExpenses; 